/**
 * @file modules/browser_extension_modal.js
 * @description Opens the browser extension install modal on page load (after a
 *              1s delay) when it's present in the DOM, dispatches shop:track
 *              CustomEvents for impression/click/dismiss, and sets a 3-day
 *              dismissal cookie on any close. The click event also carries the
 *              server-rendered params on data-click-params (page type,
 *              destination URL, signed-in state, store).
 */

import { dispatchTrack } from "../analytics/shop-analytics-track.js";

const MODAL_ID = "browser-extension-modal";
const DISMISS_COOKIE = "idme-extension-modal-dismissed";
const DISMISS_DAYS = 3;
const OPEN_DELAY_MS = 1000;

function track(modal, event, extra = {}) {
  const source = modal.dataset.source || "Unknown";
  dispatchTrack(modal, event, { source, ...extra });
}

// data-click-params is JSON from BrowserExtensionHelper#browser_extension_click_params. A
// missing or malformed value shouldn't stop the click from being tracked, so fall back to {}.
function clickParams(modal) {
  try {
    return JSON.parse(modal.dataset.clickParams || "{}");
  } catch {
    return {};
  }
}

function setDismissCookie() {
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + DISMISS_DAYS);
  document.cookie = `${DISMISS_COOKIE}=true; path=/; expires=${expiry.toUTCString()}`;
}

export function initBrowserExtensionModal() {
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  // Track which path closed the modal so the close handler can distinguish
  // a CTA conversion ("click") from a true dismissal (backdrop / X / Esc).
  let closedByPrimary = false;

  const primaryButton = modal.querySelector(
    "fragno-button[slot='primary-action']",
  );
  if (primaryButton) {
    primaryButton.addEventListener(
      "click",
      () => {
        const label = primaryButton.getAttribute("label") || "";
        track(modal, "extension_modal_click", {
          label,
          ...clickParams(modal),
        });
        closedByPrimary = true;
        modal.removeAttribute("open");
      },
      { once: true },
    );
  }

  modal.addEventListener(
    "fragno-modal-open",
    () => track(modal, "extension_modal_impression"),
    { once: true },
  );

  modal.addEventListener(
    "fragno-modal-close",
    () => {
      if (!closedByPrimary) {
        track(modal, "extension_modal_dismissed");
      }
      setDismissCookie();
    },
    { once: true },
  );

  setTimeout(() => modal.setAttribute("open", ""), OPEN_DELAY_MS);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBrowserExtensionModal);
} else {
  initBrowserExtensionModal();
}
